/**
  All of the routes for the React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav.
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

//  React layouts

// @mui icons
import Icon from '@mui/material/Icon';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SignIn from 'layouts/authentication/sign-in';
import Test from 'layouts/Test/Test';
import SignUp from 'layouts/authentication/sign-up';
import ResetPassword from 'layouts/authentication/reset-password';
import ControllersListPage from './layouts/ControllersListPage';
import ControllerPage from './layouts/ControllerPage';
import HeatMapPage from 'layouts/HeatMapPage/HeatMapPage.js';
import Insights from 'layouts/Insights/Insights.js';
import Notifications from 'layouts/notifications';
import OccupancyAverages from 'layouts/OccupancyAverages/OccupancyAverages.js';
import Tables from 'layouts/SensorPage';
import Unauthorized from 'layouts/unauthorized';
import ProtectedRoute from './components/ProtectedRoute';
import FloorsMap from './layouts/FloorsMap/FloorsMap';
import NewHomePage from './layouts/NewHomePage/NewHomePage';
import ManageSensors from './layouts/ManageSensors/ManageSensors';
import ManageControllers from './layouts/ManageControllers/ManageControllers';
import ManageSchedules from './layouts/ManageSchedules/ManageSchedules';
import ScheduleForm from './layouts/ManageSchedules/Form/ScheduleForm';
import NewScheduleForm from './layouts/ManageSchedules/Form/NewScheduleForm';
import DeviceForm from './layouts/ManageSensors/DeviceForm';
import ManageBuildings from './layouts/ManageBuildings/ManageBuildings';
import BuildingForm from './layouts/ManageBuildings/BuildingForm';
import ManageUsers from './layouts/ManageUsers/ManageUsers';
import UserForm from './layouts/ManageUsers/UserForm';
import { ManagePermissionDevicesPage, ManagePermissionUsersPage } from 'layouts/ManagePermissions/ManagePermissions';
import AdminCenter from 'layouts/AdminCenter/AdminCenter';
import DeviceSetPointHistory from 'layouts/AdminCenter/DeviceSetPointHistory/DeviceSetPointHistory';
import InactiveSensorsReport from 'layouts/AdminCenter/InactiveSensorsReport/InactiveSensorsReport';
import Statistics from 'layouts/AdminCenter/Statistics/Statistics';
import SolinUpdaterTrigger from 'layouts/AdminCenter/SolinUpdaterTrigger/SolinUpdaterTrigger';
import PermissionForm from 'layouts/ManagePermissions/PermissionForm';
import { SensorsListPage } from 'layouts/SensorsListPage/SensorsListPage';
import BuildingDemand from 'layouts/BuildingDemand/BuildingDemand';

const ROLES = {
  Admin: 5150,
  Group: 1984,
  Client: 2001,
};

const routes = [
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Test',
    key: 'test',
    icon: <Icon fontSize="small">test</Icon>,
    route: '/test',
    component: <Test />,
  },
  // ===== HOME ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Home',
    key: 'home',
    icon: <Icon fontSize="small">home</Icon>,
    route: '/home',
    component: <NewHomePage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Home',
    key: 'home-with-params',
    icon: <Icon fontSize="small">home</Icon>,
    route: '/home/:customer_id/:building_id/:floor',
    component: <NewHomePage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Home',
    key: 'home-legacy',
    icon: <Icon fontSize="small">home</Icon>,
    route: '/home/:dist_id/:cust_name/:building/:floor_number/:area_name/',
    component: <NewHomePage />,
  },

  // ===== DEVICES ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Devices-list',
    key: 'sensors-list',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/sensors-list',
    component: <SensorsListPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Devices-list',
    key: 'sensors-list-with-params',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/sensors-list/:cust_name/:building/:floor_number/:area_name',
    component: <SensorsListPage />,
  },

  // ===== HEAT MAP ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Heat Map Explorer',
    key: 'heat-map-base',
    icon: <Icon fontSize="small">thermostat</Icon>,
    route: '/heat-map',
    component: <HeatMapPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Heat Map Explorer',
    key: 'heat-map-customer',
    icon: <Icon fontSize="small">thermostat</Icon>,
    route: '/heat-map/:cust_name',
    component: <HeatMapPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Heat Map Explorer',
    key: 'heat-map-building',
    icon: <Icon fontSize="small">thermostat</Icon>,
    route: '/heat-map/:cust_name/:building',
    component: <HeatMapPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Heat Map Explorer',
    key: 'heat-map-floor',
    icon: <Icon fontSize="small">thermostat</Icon>,
    route: '/heat-map/:cust_name/:building/:floor_number',
    component: <HeatMapPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Heat Map Explorer ',
    key: 'sensorPage',
    icon: <Icon fontSize="small">table_view</Icon>,
    route: '/heat-map/',
    component: <HeatMapPage />,
  },

  // ===== INSIGHTS ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-customer',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:cust_name',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-building',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:cust_name/:building',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-floor',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:cust_name/:building/:floor_number',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-area',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:cust_name/:building/:floor_number/:area_name',
    component: <Insights />,
  },
  // Legacy routes with dist_id
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-legacy-customer',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:dist_id/:cust_name/',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-legacy-building',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:dist_id/:cust_name/:building/',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-legacy-floor',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:dist_id/:cust_name/:building/:floor_number/',
    component: <Insights />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Insights',
    key: 'insights-legacy-area',
    icon: <Icon fontSize="small">insights</Icon>,
    route: '/insights/:dist_id/:cust_name/:building/:floor_number/:area_name/',
    component: <Insights />,
  },

  // ===== OCCUPANCY ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-customer',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:cust_name',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-building',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:cust_name/:building',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-floor',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:cust_name/:building/:floor_number',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-area',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:cust_name/:building/:floor_number/:area_name',
    component: <OccupancyAverages />,
  },
  // Legacy routes with dist_id
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-legacy-customer',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:dist_id/:cust_name/',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-legacy-building',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:dist_id/:cust_name/:building/',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-legacy-floor',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:dist_id/:cust_name/:building/:floor_number/',
    component: <OccupancyAverages />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Occupancy',
    key: 'occupancy-legacy-area',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/occupancy/:dist_id/:cust_name/:building/:floor_number/:area_name/',
    component: <OccupancyAverages />,
  },

  // ===== DATA EXPLORER ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Data Explorer',
    key: 'data-explorer',
    icon: <Icon fontSize="small">table_view</Icon>,
    route: '/data-explorer/:id',
    component: <Tables />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Data Explorer',
    key: 'data-explorer-enhanced',
    icon: <Icon fontSize="small">table_view</Icon>,
    route: '/data-explorer/:buildingId?/:floor?/:area?',
    component: <Tables />,
  },

  // ===== CONTROLLER ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Controllers',
    key: 'controllers',
    icon: <Icon fontSize="small">control_camera</Icon>,
    route: '/controllers',
    component: <ControllerPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Controllers List',
    key: 'controllers-list',
    icon: <Icon fontSize="small">list</Icon>,
    route: '/controllers-list/',
    component: <ControllersListPage />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Controller Page',
    key: 'controller-page',
    icon: <Icon fontSize="small">settings</Icon>,
    route: '/controller/:id',
    component: <ControllerPage />,
  },

  // ===== FLOOR MAP ROUTES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Floor Map',
    key: 'floor-map',
    icon: <Icon fontSize="small">map</Icon>,
    route: '/floor-map',
    component: <FloorsMap />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Floors Map',
    key: 'floors-map',
    icon: <Icon fontSize="small">map</Icon>,
    route: '/floors-map/',
    component: <FloorsMap />,
  },

  // ===== DEVICES MANAGEMENT =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Sensors',
    key: 'manage-sensors',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/manage-sensors',
    component: <ManageSensors />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Controllers',
    key: 'manage-controllers',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/manage-controllers',
    component: <ManageControllers />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Device',
    key: 'manage-device',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/manage-device/:deviceType/:deviceId',
    component: <DeviceForm />,
  },
  // ===== BUILDING MANAGEMENT =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Buildings',
    key: 'manage-buildings',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/manage-buildings/',
    component: <ManageBuildings />,
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Building',
    key: 'manage-building',
    icon: <Icon fontSize="small">devices</Icon>,
    route: '/manage-buildings/:buildingId',
    component: <BuildingForm />,
  },

  // ===== USER MANAGEMENT =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Users',
    key: 'manage-users',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-users/',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd']}>
        <ManageUsers />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Users',
    key: 'manage-users',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-users/:userId',
    component: <UserForm />,
  },

  // ===== PERMISSION MANAGEMENT =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Device Permissions',
    key: 'manage-device-permissions',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-permissions/devices',
    component: (
      <ProtectedRoute allowedRoles={['s']}>
        <ManagePermissionDevicesPage />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'User Permissions',
    key: 'manage-user-permissions',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-permissions/users',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd']}>
        <ManagePermissionUsersPage />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'User Permissions',
    key: 'manage-user-permissions-form',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-permissions/users/:userId',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd']}>
        <PermissionForm />
      </ProtectedRoute>
    ),
  },
  // ===== SCHEDULES =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Schedules',
    key: 'manage-schedules',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-schedules/',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd', 'c']}>
        <ManageSchedules />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Add New Schedule',
    key: '/manage-schedules/new',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-schedules/new',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd', 'c']}>
        <NewScheduleForm />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Manage Schedules',
    key: 'manage-schedule',
    icon: <Icon fontSize="small">people</Icon>,
    route: '/manage-schedules/:scheduleId',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd', 'c']}>
        <ScheduleForm />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Admin Center',
    key: 'admin-center',
    icon: <ApartmentIcon sx={{}} />,
    route: '/admin-center/',
    component: (
      <ProtectedRoute allowedRoles={['s']}>
        <AdminCenter />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Building demand',
    key: 'building-demand',
    icon: <ApartmentIcon sx={{}} />,
    route: '/building-demand/',
    component: (
      <ProtectedRoute allowedRoles={['s', 'd', 'c']}>
        <BuildingDemand />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Inactive Sensors Report',
    key: 'inactive-devices-report',
    icon: <Icon fontSize="small">sensors_off</Icon>,
    route: '/inactive-devices-report',
    component: (
      <ProtectedRoute allowedRoles={['s']}>
        <InactiveSensorsReport />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Immediate Updates',
    key: 'solin-updater-trigger',
    icon: <Icon fontSize="small">update</Icon>,
    route: '/solin-updater-trigger/',
    component: (
      <ProtectedRoute allowedRoles={['s']}>
        <SolinUpdaterTrigger />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Device Set Point History',
    key: 'device-set-point-history',
    icon: <Icon fontSize="small">history</Icon>,
    route: '/device-set-point-history/',
    component: (
      <ProtectedRoute allowedRoles={['s']}>
        <DeviceSetPointHistory />
      </ProtectedRoute>
    ),
  },
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Statistics',
    key: 'statistics',
    icon: <Icon fontSize="small">query_stats</Icon>,
    route: '/statistics/',
    component: (
      <ProtectedRoute allowedRoles={['s']}>
        <Statistics />
      </ProtectedRoute>
    ),
  },
  // ===== NOTIFICATIONS =====
  {
    type: 'collapse',
    protected: true,
    roles: [ROLES.Client],
    name: 'Notifications',
    key: 'notifications',
    icon: <Icon fontSize="small">notifications</Icon>,
    route: '/notifications',
    component: <Notifications />,
  },

  // ===== OTHER ROUTES =====
  {
    protected: false,
    roles: [ROLES.Client],
    key: 'unauthorized',
    route: '/unauthorized',
    component: <Unauthorized />,
  },

  // ===== AUTHENTICATION =====
  {
    type: 'collapse',
    protected: false,
    roles: [ROLES.Client],
    name: 'Sign In',
    key: 'sign-in',
    icon: <Icon fontSize="small">login</Icon>,
    route: '/authentication/sign-in',
    component: <SignIn />,
  },
  {
    type: 'collapse',
    protected: false,
    roles: [ROLES.Client],
    name: 'Reset Password',
    key: 'reset-password',
    icon: <Icon fontSize="small">assignment</Icon>,
    route: '/reset-password',
    component: <ResetPassword />,
  },
  {
    type: 'collapse',
    protected: false,
    roles: [ROLES.Client],
    name: 'Sign Up',
    key: 'sign-up',
    icon: <Icon fontSize="small">assignment</Icon>,
    route: '/authentication/sign-up',
    component: <SignUp />,
  },
];

export default routes;
